import styled from 'styled-components';
import { FeatureCollection } from 'geojson';
import { useState } from 'react';
import {
  ColorKey,
  LegendItem,
  SoilAttribute,
  SoilAttributeData,
} from '../../types/soilAttributes';
import UnderlinedBlueButton from '../basics/UnderlinedBlueButton';
import SoilAttributeTable from '../soil-attributes/SoilAttributeTable';
import SoilAttributeDropdown from '../soil-attributes/SoilAttributeDropdown';
import SoilDiscreteKey from '../soil-attributes/SoilDiscreteKey';
import LearnAboutAttributeButton from '../soil-attributes/LearnAboutAttributeButton';

const AnalysisCardContainer = styled.div`
  position: absolute;
  top: 2rem;
  right: 2rem;
  z-index: 10;
  min-width: 180px;
  max-width: 300px;
  width: 100%;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.12);
  padding: 1rem 1rem 1rem 1rem;
  pointer-events: auto;
`;

const AnalysisCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
`;

const CardDropdownStyles = styled.div`
  .dropdown > button,
  .dropdown > .btn,
  .dropdown > .btn-light {
    background: #fff !important;
    color: #23272f !important;
    border: 1px solid #e5e7eb !important;
    border-radius: 10px !important;
    padding: 0.45rem 1.1rem 0.45rem 0.9rem !important;
    font-size: 0.8rem !important;
    font-weight: 500 !important;
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.04) !important;
    cursor: pointer !important;
    transition: background 0.12s, border 0.12s !important;
    display: flex !important;
    align-items: center !important;
    min-width: 0 !important;
  }

  .dropdown > button:hover,
  .dropdown > button:focus,
  .dropdown > .btn:hover,
  .dropdown > .btn:focus,
  .dropdown > .btn-light:hover,
  .dropdown > .btn-light:focus {
    background: #f3f6fa !important;
    border-color: #b3c2d1 !important;
  }
`;

// Add a modal wrapper for the table
const SoilTableModalWrapper = styled.div`
  position: fixed;
  z-index: 11000;
  top: 10vh;
  left: 25%;
  width: 60vw;
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
`;

interface SoilDataCardProps {
  activeAttribute: SoilAttribute;
  activeData: Nullable<SoilAttributeData>;
  colorKey?: Nullable<ColorKey>;
  data: FeatureCollection;
  legend?: Nullable<LegendItem[]>;
  loading: boolean;
  setActiveAttribute: (attr: SoilAttribute) => void;
}

const SoilDataCard = ({
  activeAttribute,
  activeData,
  colorKey,
  data,
  legend,
  loading,
  setActiveAttribute,
}: SoilDataCardProps) => {
  const [tableShown, setTableShown] = useState(false);
  return (
    <>
      {tableShown && (
        <SoilTableModalWrapper>
          <SoilAttributeTable
            activeAttribute={activeAttribute}
            activeData={activeData}
            selectedLayerFeatureIds={[]}
            soilPolygons={data}
            setTableShown={setTableShown}
            tableShown={tableShown}
          />
        </SoilTableModalWrapper>
      )}
      <AnalysisCardContainer>
        <AnalysisCardHeader>
          Soil Data
          <UnderlinedBlueButton
            className="font-tiny"
            color="link"
            onClick={() => setTableShown(true)}
            size="sm"
          >
            Open attribute data
          </UnderlinedBlueButton>
        </AnalysisCardHeader>
        <CardDropdownStyles>
          <SoilAttributeDropdown
            activeAttribute={activeAttribute}
            setActiveAttribute={setActiveAttribute}
          />
        </CardDropdownStyles>
        {loading || activeData?.loading ? (
          <div>Loading...</div>
        ) : (
          <>
            <SoilDiscreteKey
              activeAttribute={activeAttribute}
              activeData={activeData}
              colorKey={colorKey}
              data={data}
              legend={legend}
            />
            <LearnAboutAttributeButton activeData={activeData} />
          </>
        )}
      </AnalysisCardContainer>
    </>
  );
};

export default SoilDataCard;
